// internal
import { TypeAssert } from '@creejs/commons-lang'
// owned
import { DefaultLevel } from './log-level.js'
// eslint-disable-next-line no-unused-vars
import LogFactory from './log-factory.js'
import Provider from './provider.js'

const { assertString } = TypeAssert

/**
 * @module LogConfig
 * @description Provide Impl of Log Configuration
 * @private
 */

/**
 * type name of current provider
 * @type {string}
 * @public
 */
// @ts-ignore
// eslint-disable-next-line prefer-const
let currentProvider = null

/**
 * Global logging level
 * @type {number}
 * @public
 */
// @ts-ignore
// eslint-disable-next-line prefer-const
let currentLevel = DefaultLevel

/**
 * The map of registered logging libraries
 * @type {Map<string,Provider>}
 * @private
 */
const type2Provider = new Map()

/**
 * Adds a logging provider for the specified type.
 * @param {string} typeName - The type identifier for the provider.
 * @param {Provider} provider - The logging provider implementation.
 * @returns {void}
 * @alias module:LogConfig.addProvider
 */
function addProvider (typeName, provider) {
  assertString(typeName)
  Provider.assertProviderLike(provider)
  type2Provider.set(typeName.toUpperCase(), provider)
}

/**
 * Gets the logging provider for the specified type name.
 * @param {string} typeName - The type name to look up in the provider map.
 * @returns {Provider|undefined} The logging provider instance if found, otherwise undefined.
 */
function getProvider (typeName) {
  assertString(typeName)
  return type2Provider.get(typeName.toUpperCase())
}

/**
 * Removes a logging provider of the specified type.
 * @param {string} typeName - The type name of the provider to remove.
 * @returns {boolean}
 */
function removeProvider (typeName) {
  assertString(typeName)
  return type2Provider.delete(typeName.toUpperCase())
}

function clearProviders () {
  type2Provider.clear()
}

/**
 * Returns an array of all registered factory types and intances.
 * 1. Each entry is a [type, factory] pair
 * @returns {Array<{0:string, 1:Provider}>} An array of factory entries.
 */
function listProviders () {
  return [...type2Provider.entries()]
}

/**
 * Created LogFactories Index with type name
 * @type {Map<string, LogFactory>}
 * @public
 */
const type2Factory = new Map()

/**
 * Registers a log factory for a given type name.
 * @param {string} typeName - The name of the log type to register.
 * @param {LogFactory} logFactory - Factory function that creates a logger for the specified type.
 */
function addFactory (typeName, logFactory) {
  assertString(typeName)
  LogFactory.assertLogFactoryLike(logFactory)
  type2Factory.set(typeName.toUpperCase(), logFactory)
}

/**
 * Removes a factory of the given type from the type-to-factory mapping.
 * @param {string} typeName - The name of the type whose factory should be removed.
 * @returns {boolean}
 */
function removeFactory (typeName) {
  assertString(typeName)
  return type2Factory.delete(typeName.toUpperCase())
}

/**
 * Gets the factory function associated with the given type name.
 * @param {string} typeName - The name of the type to look up.
 * @returns {LogFactory|undefined} The factory function for the specified type.
 */
function getFactory (typeName) {
  assertString(typeName)
  return type2Factory.get(typeName.toUpperCase())
}

/**
 * Clears all registered factories from the type2Factory instance.
 * This is typically used to reset the factory state during testing or teardown.
 */
function clearFactories () {
  type2Factory.clear()
}

/**
 * Returns an array of all registered factory types and intances.
 * 1. Each entry is a [type, factory] pair
 * @returns {Array<{0:string, 1:LogFactory}>} An array of factory entries.
 */
function listFactories () {
  return [...type2Factory.entries()]
}

function clear () {
  clearProviders()
  clearFactories()
}

export default {
  currentProvider,
  currentLevel,
  type2Provider,
  type2Factory,
  addProvider,
  getProvider,
  removeProvider,
  clearProviders,
  listProviders,
  addFactory,
  getFactory,
  removeFactory,
  clearFactories,
  listFactories,
  clear
}

export {
  currentProvider,
  currentLevel,
  type2Provider,
  type2Factory,
  addProvider,
  getProvider,
  removeProvider,
  clearProviders,
  listProviders,
  addFactory,
  getFactory,
  removeFactory,
  clearFactories,
  listFactories,
  clear
}
